import React, { useMemo, useState } from 'react';
import { Alert, InputNumber, Modal, Radio, Select, Space, Typography, message } from 'antd';
import _ from 'lodash';
import {
  InternalTransferBody,
  InternalTransferResult,
  OperationWallet,
  OperationWalletProvider,
} from '../../domain/usdt-providers/operationWalletProvider';
import { Environment } from '../../core/configs/services';

/** 輪詢:內轉是非同步的,送出只代表「受理」,不代表「完成」。 */
const POLL_MS = 3000;
const POLL_MAX = 120; // 6 分鐘 —— 一次 sweep 最壞情況(等錢包乾淨 300s x 2 段)還有餘裕

function tronscanTxUrl(txId: string): string {
  const env = Environment() ?? 'develop';
  const base = _.find(['prod', 'stg'], (x) => x === env)
    ? 'https://tronscan.org/#/transaction/'
    : 'https://shasta.tronscan.org/#/transaction/';
  return base + txId;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const { Text } = Typography;

/**
 * TRX left behind when you press「全部」on a TRX transfer.
 *
 * Just enough to cover the transfer's own bandwidth burn (~0.268 TRX when the wallet has no
 * delegated bandwidth) — sweep to literally zero and the transfer cannot fund itself. It used
 * to be 5 to stay above poke-jobs' auto-disable floor (PAYOUT_TRX_MIN_DISABLE=2), but internal
 * transfer now refuses an enabled payout wallet, so the source is always disabled already and
 * dropping below the floor is a non-issue. 0.5 leaves ~0.23 after the burn.
 */
const TRX_RESERVE = 0.5;

type Props = {
  open: boolean;
  source: OperationWallet | null;
  wallets: OperationWallet[];
  onClose: () => void;
  onDone: () => void;
};

const InternalTransferModal = (props: Props) => {
  const { open, source, wallets, onClose, onDone } = props;
  const [mode, setMode] = useState<'sweep' | 'amount'>('sweep');
  const [token, setToken] = useState<'USDT' | 'TRX'>('USDT');
  const [toAddress, setToAddress] = useState<string | undefined>();
  const [amount, setAmount] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [phase, setPhase] = useState<'idle' | 'processing'>('idle');
  const [result, setResult] = useState<InternalTransferResult | null>(null);

  // Generated once per opened dialog, not per click: it is what makes a double-click land
  // as one transfer instead of two. The backend dedups on it.
  const requestKey = useMemo(
    () => (source ? `${source.address}-${Date.now()}` : ''),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [source, open],
  );

  const targets = wallets.filter((w) => w.address !== source?.address);

  const available =
    token === 'USDT' ? Number(source?.balance ?? 0) : Number(source?.trxBalance ?? 0);

  const max = token === 'USDT' ? available : Math.max(0, available - TRX_RESERVE);

  const reset = () => {
    setMode('sweep');
    setToken('USDT');
    setToAddress(undefined);
    setAmount(null);
    setPhase('idle');
    setResult(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    if (!source || !toAddress) {
      message.warning('請選擇目標錢包');
      return;
    }
    if (mode === 'amount' && (!amount || amount <= 0)) {
      message.warning('請填寫金額');
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const body: InternalTransferBody =
        mode === 'sweep'
          ? { fromAddress: source.address, toAddress, mode: 'sweep', requestKey }
          : {
              fromAddress: source.address,
              toAddress,
              mode: 'amount',
              token,
              amount: amount as number,
              requestKey,
            };

      // 送出只是「受理」。真正的轉帳在背景跑 —— 一次 sweep 可能要好幾分鐘。
      await OperationWalletProvider.internalTransfer(body);
      setPhase('processing');

      for (let i = 0; i < POLL_MAX; i++) {
        await sleep(POLL_MS);
        const st = (await OperationWalletProvider.internalTransferStatus(requestKey)).data;
        if (st?.status === 'done') {
          setResult(st.result);
          onDone(); // 重新載入列表(餘額已在後端刷新)
          return;
        }
        if (st?.status === 'failed') {
          message.error(st.error || '內轉失敗');
          setPhase('idle');
          return;
        }
      }
      message.warning('仍在處理中 —— 請稍後在列表確認餘額,或到 tronscan 查看來源錢包');
      setPhase('idle');
    } catch (e: any) {
      message.error(
        e?.response?.data?.displayMessage || e?.response?.data?.message || e?.message || '內轉失敗',
      );
      setPhase('idle');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="運營錢包內轉"
      visible={open}
      onOk={result ? close : submit}
      onCancel={close}
      okText={result ? '關閉' : '確認轉出'}
      cancelButtonProps={{ style: result ? { display: 'none' } : undefined }}
      confirmLoading={submitting}
      destroyOnClose
    >
      {result && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert type="success" showIcon message="轉帳已送出上鏈" />
          {result.usdt && (
            <div>
              <Text type="secondary">USDT {result.usdt.amount}</Text>
              <div>
                {/* eslint-disable-next-line react/jsx-no-target-blank */}
                <a href={tronscanTxUrl(result.usdt.txId)} target="_blank" rel="noreferrer">
                  {result.usdt.txId}
                </a>
              </div>
            </div>
          )}
          {result.trx && (
            <div>
              <Text type="secondary">TRX {result.trx.amount}</Text>
              <div>
                {/* eslint-disable-next-line react/jsx-no-target-blank */}
                <a href={tronscanTxUrl(result.trx.txId)} target="_blank" rel="noreferrer">
                  {result.trx.txId}
                </a>
              </div>
            </div>
          )}
          {result.txId && (
            <div>
              {/* eslint-disable-next-line react/jsx-no-target-blank */}
              <a href={tronscanTxUrl(result.txId)} target="_blank" rel="noreferrer">
                {result.txId}
              </a>
            </div>
          )}
        </Space>
      )}
      {!result && phase === 'processing' && (
        <Alert
          type="info"
          showIcon
          message="處理中…"
          description="轉帳已受理,正在鏈上執行(先 USDT 再 TRX)。完成後這裡會顯示交易連結,TG 也會收到通知。這個視窗可以關掉。"
        />
      )}
      {!result && phase === 'idle' && (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Text type="secondary">來源</Text>
          <div>
            <Text code>{source?.address}</Text>
          </div>
        </div>

        <div>
          <Text type="secondary">轉出方式</Text>
          <div>
            <Radio.Group
              value={mode}
              onChange={(e) => {
                setMode(e.target.value);
                setAmount(null);
              }}
            >
              <Radio.Button value="sweep">全部清空(USDT + TRX)</Radio.Button>
              <Radio.Button value="amount">指定幣種與金額</Radio.Button>
            </Radio.Group>
          </div>
          {mode === 'sweep' && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              用公司的能量與帶寬，把這個錢包的餘額<b>全部</b>轉走：先 USDT，再 TRX，
              <b>TRX 清到 0、一毛不留</b>。兩筆轉帳的帶寬（345 + 270）會一次向 RENTAL
              借齊，所以 TRX 那筆不用自己燒手續費。
              <br />
              （RENTAL 帶寬不足、借不到時，TRX 會保留 0.5 顆去付自己的手續費 —— 轉完會告訴你。）
              <br />
              USDT 那筆若失敗，TRX 不會轉 —— 錢包只剩 USDT、沒有 TRX 的話，它連 C 的
              0.01 TRX 都付不出來，那些 USDT 會永遠轉不出去。
            </Text>
          )}
        </div>

        {mode === 'amount' && (
          <div>
            <Text type="secondary">幣種</Text>
            <div>
              <Radio.Group
                value={token}
                onChange={(e) => {
                  setToken(e.target.value);
                  setAmount(null);
                }}
              >
                <Radio.Button value="USDT">USDT</Radio.Button>
                <Radio.Button value="TRX">TRX</Radio.Button>
              </Radio.Group>
            </div>
          </div>
        )}

        <div>
          <Text type="secondary">目標錢包(只能轉給自家運營錢包)</Text>
          <Select
            style={{ width: '100%' }}
            placeholder="選擇目標(可輸入地址搜尋)"
            value={toAddress}
            onChange={setToAddress}
            showSearch
            filterOption={(input, option) =>
              String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={targets.map((w) => ({
              value: w.address,
              label: `${w.address} (${w.type}) — USDT ${Number(w.balance ?? 0).toLocaleString()} / TRX ${Number(
                w.trxBalance ?? 0,
              ).toLocaleString()}`,
            }))}
          />
        </div>

        {mode === 'amount' && (
          <div>
            <Text type="secondary">
              金額(可用 {available.toLocaleString(undefined, { maximumFractionDigits: 6 })} {token})
            </Text>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <InputNumber
                style={{ flex: 1 }}
                min={0}
                max={max}
                value={amount ?? undefined}
                onChange={(v) => setAmount((v as number) ?? null)}
                placeholder="轉出金額"
              />
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a style={{ paddingLeft: 8 }} onClick={() => setAmount(max)}>
                全部
              </a>
            </div>
            {token === 'TRX' && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                「全部」會保留 {TRX_RESERVE} TRX —— 這筆轉帳沒帶寬時要自燒 ~0.268 TRX 當手續費
              </Text>
            )}
          </div>
        )}

        <Alert
          type="warning"
          showIcon
          message="此操作會實際上鏈"
          description={
            !toAddress
              ? '請先選擇目標錢包,送出後無法撤回'
              : mode === 'sweep'
              ? `把 ${source?.address} 的「全部 USDT + 全部 TRX」轉到 ${toAddress}(先 U 再 TRX)`
              : amount
              ? `${amount} ${token} 從 ${source?.address} → ${toAddress}`
              : '請確認金額與目標無誤,送出後無法撤回'
          }
        />
      </Space>
      )}
    </Modal>
  );
};

export default InternalTransferModal;
